import { z } from 'zod';
import { CameraStatus, ZoneKind } from './enums.js';
/**
 * Camera DTOs.
 *
 * There is deliberately no password field anywhere in `Camera`. The browser is
 * never given a credential, an RTSP URL, or anything from which one could be
 * reconstructed. Credentials are write-only over the API and are stored
 * AES-256-GCM encrypted; live viewing goes through go2rtc, which holds the real
 * URL server-side.
 */
export const Camera = z.object({
    id: z.string().uuid(),
    name: z.string(),
    location: z.string(),
    zoneKind: ZoneKind,
    enabled: z.boolean(),
    status: CameraStatus,
    statusReason: z.string().nullable(),
    /** Redacted host label for display, e.g. "192.168.1.64" — never credentials. */
    host: z.string(),
    hasCredentials: z.boolean(),
    /** Stream name used by go2rtc; the URL behind it is server-side only. */
    streamKey: z.string(),
    inferenceFps: z.number(),
    inferenceImgsz: z.number().int(),
    confidenceThreshold: z.number(),
    motionGate: z.boolean(),
    recordingByNvr: z.boolean(),
    lastSeenAt: z.string().datetime().nullable(),
    lastFrameAt: z.string().datetime().nullable(),
    currentFps: z.number().nullable(),
    currentOccupancy: z.number().int().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
const hostname = z
    .string()
    .min(1)
    .max(253)
    .regex(/^[a-zA-Z0-9._-]+$/, 'Host must be a hostname or IPv4 address, no scheme or credentials');
const rtspPath = z
    .string()
    .max(256)
    .regex(/^\/[^\s?#]*(\?[^\s#]*)?$/, 'Path must start with / and contain no whitespace')
    .or(z.literal(''));
export const CreateCameraBody = z.object({
    name: z.string().min(1).max(120),
    location: z.string().min(1).max(200),
    zoneKind: ZoneKind,
    host: hostname,
    port: z.number().int().min(1).max(65535).default(554),
    pathMain: rtspPath.default('/Streaming/Channels/101'),
    pathSub: rtspPath.default('/Streaming/Channels/102'),
    username: z.string().max(120).default(''),
    /** Write-only. Never returned by any endpoint. */
    password: z.string().max(256).default(''),
    enabled: z.boolean().default(true),
    inferenceFps: z.number().min(0.5).max(15).default(4),
    inferenceImgsz: z.number().int().min(320).max(1280).default(640),
    confidenceThreshold: z.number().min(0.05).max(0.95).default(0.4),
    motionGate: z.boolean().default(true),
    recordingByNvr: z.boolean().default(true),
});
export const UpdateCameraBody = CreateCameraBody.partial();
/* -------------------------------------------------------------------------- */
/* Zones and lines                                                            */
/* -------------------------------------------------------------------------- */
/** A polygon point in normalised frame coordinates (0..1). */
export const ZonePoint = z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) });
export const CameraZone = z.object({
    id: z.string().uuid(),
    cameraId: z.string().uuid(),
    name: z.string(),
    kind: ZoneKind,
    polygon: z.array(ZonePoint).min(3),
    /** Occupancy above this for `crowdSustainSeconds` raises CROWD_THRESHOLD. */
    crowdThreshold: z.number().int().nullable(),
    crowdSustainSeconds: z.number().int(),
    /** Any presence here raises RESTRICTED_AREA_ENTRY during restricted windows. */
    restricted: z.boolean(),
    /** Seats/stations the zone represents, used for utilisation percentages. */
    capacity: z.number().int().nullable(),
    enabled: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateZoneBody = z.object({
    name: z.string().min(1).max(120),
    kind: ZoneKind,
    polygon: z.array(ZonePoint).min(3).max(64),
    crowdThreshold: z.number().int().min(1).max(500).nullable().default(null),
    crowdSustainSeconds: z.number().int().min(5).max(3600).default(60),
    restricted: z.boolean().default(false),
    capacity: z.number().int().min(1).max(500).nullable().default(null),
    enabled: z.boolean().default(true),
});
export const ZoneLine = z.object({
    id: z.string().uuid(),
    cameraId: z.string().uuid(),
    zoneId: z.string().uuid().nullable(),
    name: z.string(),
    start: ZonePoint,
    end: ZonePoint,
    /** Which side of the line counts as "IN" (into the venue). */
    inwardNormal: ZonePoint,
    countsVisitors: z.boolean(),
    enabled: z.boolean(),
    createdAt: z.string().datetime(),
});
export const CreateLineBody = z.object({
    name: z.string().min(1).max(120),
    zoneId: z.string().uuid().nullable().default(null),
    start: ZonePoint,
    end: ZonePoint,
    inwardNormal: ZonePoint,
    countsVisitors: z.boolean().default(true),
    enabled: z.boolean().default(true),
});
/**
 * The full camera runtime config handed to the vision service. This crosses a
 * trust boundary that stays inside the LAN: it *does* contain the decrypted
 * RTSP URL, so it is served only to the vision service over the internal network
 * with a service token, and is never routed through the browser-facing API
 * surface.
 */
export const CameraRuntimeConfig = z.object({
    id: z.string().uuid(),
    name: z.string(),
    rtspUrl: z.string(),
    streamKey: z.string(),
    enabled: z.boolean(),
    inferenceFps: z.number(),
    inferenceImgsz: z.number().int(),
    confidenceThreshold: z.number(),
    motionGate: z.boolean(),
    zones: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        kind: ZoneKind,
        polygon: z.array(ZonePoint),
        enabled: z.boolean(),
    })),
    lines: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        zoneId: z.string().uuid().nullable(),
        start: ZonePoint,
        end: ZonePoint,
        inwardNormal: ZonePoint,
        enabled: z.boolean(),
    })),
});
//# sourceMappingURL=cameras.js.map